import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";

export interface TimelineEventData {
  resourceType: string;
  label: string;
  date: string; // ISO yyyy-mm-dd
}

function firstDateOnly(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
  }
  return "";
}

/**
 * Only resource types that represent a discrete, dated clinical event are
 * timeline-eligible — Patient/Practitioner/Coverage/Consent/etc. don't have
 * a single meaningful "when it happened" date, so they're left out rather
 * than forced onto the timeline with a made-up date.
 */
export function adaptTimelineEvent(record: ParsedRecord): TimelineEventData | null {
  const f = record.fields;
  switch (record.resourceType) {
    case "Encounter": {
      const date = firstDateOnly(f.start_date);
      if (!date) return null;
      return { resourceType: "Encounter", label: (f.encounter_type as string) || (f.encounter_class_display as string) || "Visit", date };
    }
    case "MedicationRequest": {
      const date = firstDateOnly(f.authored_on);
      if (!date) return null;
      return { resourceType: "MedicationRequest", label: (f.medication_name as string) || "Medication", date };
    }
    case "Procedure": {
      const date = firstDateOnly(f.performed_start, f.performed_date);
      if (!date) return null;
      return { resourceType: "Procedure", label: (f.display_name as string) || "Procedure", date };
    }
    case "Immunization": {
      const date = firstDateOnly(f.occurrence_date);
      if (!date) return null;
      return { resourceType: "Immunization", label: (f.vaccine_display as string) || "Immunization", date };
    }
    case "DiagnosticReport": {
      const date = firstDateOnly(f.issued_date, f.effective_date);
      if (!date) return null;
      return { resourceType: "DiagnosticReport", label: (f.display_name as string) || "Lab Result", date };
    }
    case "Condition": {
      const date = firstDateOnly(f.onset_date, f.recorded_date, f.asserted_date);
      if (!date) return null;
      return { resourceType: "Condition", label: (f.display_name as string) || "Condition", date };
    }
    default:
      return null;
  }
}
